// Teams-style presence hook. Tracks user-selected status (manual) AND
// auto-away (idle). Caller wires the final `status` value into whatever
// signaling transport it uses (SignalR `SetStatus` invoke in our case).
//
// Why shared: Mail and Connect both need the same picker + idle logic.
import { useCallback, useEffect, useRef, useState } from "react";

export type PresenceStatus = "online" | "away" | "busy" | "dnd" | "brb" | "offline";

const MANUAL_KEY = "athavita-presence-manual";
const IDLE_AWAY_MS = 5 * 60_000; // 5 minutes

/**
 * Tracks the user's chosen presence status with two inputs:
 *   1. Manual selection, persisted to localStorage (Available / Busy / DND / BRB).
 *   2. Automatic idle detection: after 5 minutes of no pointer/keyboard
 *      activity, the effective status drops to "away" unless the user
 *      explicitly chose "busy" / "dnd" (those stay sticky — someone in
 *      DND doesn't want to look Away just because they're reading).
 *
 * The caller receives `effective` (what to publish to peers) and a
 * `setManual` setter for the picker. Changes in `effective` are the
 * signal to invoke the hub's SetStatus method.
 */
export function useSuitePresence() {
    const [manual, setManualState] = useState<PresenceStatus>(() => readManual());
    const [idle, setIdle] = useState(false);
    const idleTimerRef = useRef<number | null>(null);

    const resetIdleTimer = useCallback(() => {
        if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current);
        setIdle(false);
        idleTimerRef.current = window.setTimeout(() => setIdle(true), IDLE_AWAY_MS);
    }, []);

    // Track keyboard + pointer + visibility to reset the idle timer.
    useEffect(() => {
        if (typeof window === "undefined") return;
        resetIdleTimer();
        const events: Array<keyof WindowEventMap> = ["mousemove", "keydown", "pointerdown", "scroll", "touchstart"];
        for (const name of events) window.addEventListener(name, resetIdleTimer, { passive: true });
        const vis = () => {
            if (document.visibilityState === "visible") resetIdleTimer();
            else setIdle(true);
        };
        document.addEventListener("visibilitychange", vis);
        return () => {
            for (const name of events) window.removeEventListener(name, resetIdleTimer);
            document.removeEventListener("visibilitychange", vis);
            if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current);
        };
    }, [resetIdleTimer]);

    const setManual = useCallback((status: PresenceStatus) => {
        setManualState(status);
        try { window.localStorage.setItem(MANUAL_KEY, status); } catch { /* ignore */ }
    }, []);

    // Effective = manual unless we're idle and the manual is "online" or
    // "brb" (transient states). "busy"/"dnd" stay sticky regardless.
    const effective: PresenceStatus =
        idle && (manual === "online" || manual === "brb") ? "away" : manual;

    return { manual, effective, idle, setManual };
}

function readManual(): PresenceStatus {
    if (typeof window === "undefined") return "online";
    try {
        const raw = window.localStorage.getItem(MANUAL_KEY);
        if (raw === "online" || raw === "away" || raw === "busy" || raw === "dnd" || raw === "brb") return raw;
    } catch { /* ignore */ }
    return "online";
}

/** Human-readable label, matching Teams' terminology exactly. */
export function presenceLabel(status: PresenceStatus): string {
    switch (status) {
        case "online": return "Available";
        case "away": return "Away";
        case "busy": return "Busy";
        case "dnd": return "Do not disturb";
        case "brb": return "Be right back";
        case "offline": return "Offline";
    }
}

/** Tailwind bg-* class for the presence dot. */
export function presenceDotClass(status: PresenceStatus): string {
    switch (status) {
        case "online": return "bg-emerald-500";
        case "away": return "bg-amber-400";
        case "busy":
        case "dnd": return "bg-rose-500";
        case "brb": return "bg-amber-400";
        default: return "bg-zinc-400";
    }
}
